import { useState, useEffect } from 'react';
import { Moon, Sparkles, Coffee } from 'lucide-react';
import Lottie from 'lottie-react';
import mortyDanceAnimation from './assets/The Morty Dance Loader.json';
import logo from './assets/logo.png';
import { fetchTestimonials, type Testimonial } from './services/discordService';

function App() {
  const [loading, setLoading] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeOut(true);
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadTestimonials = async () => {
      const data = await fetchTestimonials();
      setTestimonials(data);
    };

    loadTestimonials();
  }, []);

  if (loading) {
    return (
      <div className={`min-h-screen w-full bg-gradient-to-br from-gray-900 via-slate-800 to-black relative overflow-hidden flex items-center justify-center transition-opacity duration-800 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
        {/* Animated background elements matching main design */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
          
          {/* Floating code elements */}
          <div className="absolute top-20 left-10 text-[#f5f56f] font-mono text-sm opacity-30 animate-float">
            {'{ code }'}
          </div>
          <div className="absolute top-40 right-20 text-[#e791ce] font-mono text-sm opacity-30 animate-float-delay">
            {'</dev>'}
          </div>
          <div className="absolute bottom-32 left-20 text-[#6778af] font-mono text-sm opacity-30 animate-float-delay-2">
            {'npm run'}
          </div>
          <div className="absolute top-60 left-1/3 text-[#313131] font-mono text-sm opacity-30 animate-float">
            {'git push'}
          </div>

          {/* Floating shapes */}
          <div className="absolute top-32 right-10 animate-float">
            <Coffee className="w-8 h-8 text-[#f5f56f] opacity-40" />
          </div>
          <div className="absolute bottom-40 right-32 animate-float-delay">
            <Sparkles className="w-6 h-6 text-[#e791ce] opacity-40" />
          </div>
          <div className="absolute top-1/2 left-10 animate-float-delay-2">
            <Moon className="w-7 h-7 text-[#6778af] opacity-30" />
          </div>
        </div>

        {/* Main loading content */}
        <div className="relative">
          <div className="w-48 h-48 mx-auto mb-6">
            <Lottie
              animationData={mortyDanceAnimation}
              loop={true}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
            />
          </div>

          <div className="text-center">
            <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#f5f56f] via-[#e791ce] to-[#6778af] mb-2 animate-gradient-x">
              Loading...
            </div>
            <div className="flex justify-center gap-1 mb-4">
              <div className="w-2 h-2 bg-[#f5f56f] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-[#e791ce] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-[#6778af] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>

            <div className="w-64 mx-auto">
              <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#f5f56f] to-[#6778af] rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom decoration matching main design */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/40 to-transparent"></div>
        
        {/* Corner decorations */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-[#f5f56f]/10 to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-[#6778af]/10 to-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-800 to-black relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        
        {/* Floating code elements */}
        <div className="absolute top-20 left-10 text-[#f5f56f] font-mono text-sm opacity-30 animate-float">
          {'{ code }'}
        </div>
        <div className="absolute top-40 right-20 text-[#e791ce] font-mono text-sm opacity-30 animate-float-delay">
          {'</dev>'}
        </div>
        <div className="absolute bottom-32 left-20 text-[#6778af] font-mono text-sm opacity-30 animate-float-delay-2">
          {'npm run'}
        </div>
        <div className="absolute top-60 left-1/3 text-[#313131] font-mono text-sm opacity-30 animate-float">
          {'git push'}
        </div>

        {/* Floating shapes */}
        <div className="absolute top-32 right-10 animate-float">
          <Coffee className="w-8 h-8 text-[#f5f56f] opacity-40" />
        </div>
        <div className="absolute bottom-40 right-32 animate-float-delay">
          <Sparkles className="w-6 h-6 text-[#e791ce] opacity-40" />
        </div>
        <div className="absolute top-1/2 left-10 animate-float-delay-2">
          <Moon className="w-7 h-7 text-[#6778af] opacity-30" />
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          {/* Logo area */}
          <div className="mb-8 animate-fade-in">
            <img src={logo} alt="Logo" className="w-42 h-32 mx-auto mb-6" />
          </div>

          {/* Main title */}
          <div className="animate-slide-up">
            <h1 className="text-6xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#f5f56f] via-[#e791ce] to-[#6778af] mb-4 animate-gradient-x">
              Coming Soon
            </h1>
            <div className="h-1 w-32 bg-gradient-to-r from-[#f5f56f] to-[#6778af] mx-auto mb-8 animate-pulse"></div>
          </div>

          {/* Subtitle */}
          <div className="animate-slide-up-delay">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-2 tracking-wide">
              Sleeping Studio Shop
            </h2>
            <div className="text-sm text-[#f5f56f] font-mono mb-4 tracking-wider">
              CREATIVE DEVELOPER STUDIO
            </div>
            <p className="text-lg text-gray-300 mb-8 max-w-lg mx-auto leading-relaxed">
              Crafting digital experiences where creativity meets code.
              <br />
              <span className="text-[#f5f56f]">Something innovative is brewing...</span>
            </p>
            
            {/* Tech stack indicators */}
            <div className="flex justify-center gap-4 mb-8 text-sm text-gray-400">
              <span className="px-3 py-1 bg-gray-800/50 rounded-full border border-gray-700">Discord Bot</span>
              <span className="px-3 py-1 bg-gray-800/50 rounded-full border border-gray-700">Website POS</span>
              <span className="px-3 py-1 bg-gray-800/50 rounded-full border border-gray-700">Payment Gateway</span>
              <span className="px-3 py-1 bg-gray-800/50 rounded-full border border-gray-700">Custom Web</span>
            </div>
          </div>

          {/* Call to action */}
          <div className="animate-slide-up-delay-2">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#f5f56f]/20 to-[#e791ce]/20 backdrop-blur-sm rounded-full border border-[#f5f56f]/30 text-white hover:from-[#f5f56f]/30 hover:to-[#e791ce]/30 transition-all duration-300 cursor-pointer group">
                <span className="text-sm uppercase tracking-wider font-medium">Get Notified</span>
                <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
              </div>

              <div className="inline-flex items-center gap-3 px-8 py-4 bg-[#313131]/20 backdrop-blur-sm rounded-full border border-[#313131]/30 text-gray-300 hover:bg-[#313131]/30 transition-all duration-300 cursor-pointer group">
                <span className="text-sm uppercase tracking-wider">Our Work</span>
                <Coffee className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
              </div>

              <a href="https://ko-fi.com/typicalsleepingboyxxx" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#6778af]/20 to-[#f5f56f]/20 backdrop-blur-sm rounded-full border border-[#6778af]/30 text-white hover:from-[#6778af]/30 hover:to-[#f5f56f]/30 transition-all duration-300 cursor-pointer group">
                <span className="text-sm uppercase tracking-wider font-medium">Buy Me a Coffee</span>
                <Coffee className="w-4 h-4 group-hover:bounce transition-transform duration-300" />
              </a>

              <button onClick={() => setShowDiscordModal(true)} className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#e791ce]/20 to-[#313131]/20 backdrop-blur-sm rounded-full border border-[#e791ce]/30 text-white hover:from-[#e791ce]/30 hover:to-[#313131]/30 transition-all duration-300 cursor-pointer group">
                <span className="text-sm uppercase tracking-wider font-medium">Join Discord</span>
                <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                </svg>
              </button>
            </div>
          </div>
          
          {/* Status indicator */}
          <div className="animate-slide-up-delay-2 mt-12">
            <div className="inline-flex items-center gap-2 text-sm text-gray-400">
              <div className="w-2 h-2 bg-[#f5f56f] rounded-full animate-pulse"></div>
              <span className="font-mono">Status: In Development</span>
            </div>
          </div>

          {/* Testimonials */}
          {testimonials.length > 0 && (
            <div className="animate-slide-up-delay-2 mt-20 mb-16">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-white mb-4">Customer Testimonials</h3>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  See what our satisfied customers have to say about our services
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
                {testimonials.slice(0, 6).map((testimonial) => (
                  <div key={testimonial.id} className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:bg-gray-800/70 hover:border-gray-600/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#f5f56f]/10">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#f5f56f] to-[#e791ce] rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-base">
                          {testimonial.author.displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-semibold text-base mb-1">{testimonial.author.displayName}</div>
                        <div className="text-gray-400 text-sm">
                          {new Date(testimonial.timestamp).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Feature */}
                    <div className="mb-5">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-medium">Service</div>
                      <div className="text-white text-sm font-medium bg-gray-700/30 rounded-lg px-3 py-2">
                        {testimonial.feature}
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="mb-5">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-medium">Rating</div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className={`text-lg ${i < testimonial.rating ? 'text-[#f5f56f] drop-shadow-sm' : 'text-gray-600'}`}>
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="text-gray-400 text-sm font-medium">({testimonial.rating}/5)</span>
                      </div>
                    </div>

                    {/* Feedback */}
                    <div className="mb-5">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-medium">Feedback</div>
                      <div className="bg-gray-700/20 rounded-lg p-4">
                        <p className="text-gray-300 text-sm leading-relaxed italic">"{testimonial.feedback}"</p>
                      </div>
                    </div>

                    {/* Reactions */}
                    {testimonial.reactions > 0 && (
                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-700/30">
                        <div className="flex items-center gap-1 text-sm text-gray-400">
                          <span className="text-lg">👍</span>
                          <span className="font-medium">{testimonial.reactions}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Bottom spacing */}
              <div className="h-8"></div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/40 to-transparent"></div>

      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-[#f5f56f]/10 to-transparent"></div>
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-[#6778af]/10 to-transparent"></div>

      {/* Discord Modal */}
      {showDiscordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900/95 backdrop-blur-md rounded-2xl p-6 border border-gray-700/50 max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Join Our Discord Store</h3>
              <button
                onClick={() => setShowDiscordModal(false)}
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <iframe
              src="https://discord.com/widget?id=1247795471028846656&theme=dark"
              width="100%"
              height="400"
              allowTransparency={true}
              frameBorder="0"
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
              className="rounded-xl"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;